/**
 * Aura AGM — 문서 [[Signers]] 서명자 저장소 (varKey 별 SignerEntry[]).
 * 인코퍼레이션 sgPreSignersContext 와 동일 패턴이나 **자체완결**(독립 심볼).
 */
import { inject, provide, reactive, ref, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

/** 현재 엔티티 CD — SignersField 의 직책별 서명자 선택 다이얼로그(Director/Secretary/…)를 켜는 데 필요. */
export const AGM_ENTITY_CD: InjectionKey<Ref<string>> = Symbol('auraAgmEntityCd');
export function useAgmEntityCd(): Ref<string> {
  return inject(AGM_ENTITY_CD, ref(''));
}

export type AgmSignersCtx = {
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  clearAll: () => void;
};

export const AGM_SIGNERS: InjectionKey<AgmSignersCtx> = Symbol('auraAgmSigners');

export function provideAgmSigners(): AgmSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  const ctx: AgmSignersCtx = {
    signersOf: (k) => store[k] || [],
    setSigners: (k, list) => { store[k] = Array.isArray(list) ? list : []; },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) if (v?.length) out[k] = v;
      return out;
    },
    importAll: (map) => { if (map) for (const [k, v] of Object.entries(map)) store[k] = v; },
    clearAll: () => { for (const k of Object.keys(store)) delete store[k]; },
  };
  provide(AGM_SIGNERS, ctx);
  return ctx;
}

export function useAgmSigners(): AgmSignersCtx {
  return inject(AGM_SIGNERS, null) || {
    signersOf: () => [],
    setSigners: () => {},
    exportAll: () => ({}),
    importAll: () => {},
    clearAll: () => {},
  };
}
